import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, CalendarClock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, bannerSrc, formatIST } from '../../lib/api';
import { SectionLabel } from '../Section';
import { StatusPill, FreePill, SlotBar } from '../TournamentCard';

export default function Featured() {
  const [t, setT] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .featured()
      .then((d) => setT(d.tournament))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (loaded && !t) return null;

  const isFree = t && (t.paid_type === 'free' || !t.entry_fee);

  return (
    <section className="relative py-20 sm:py-28" data-testid="featured-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionLabel testid="featured-label">Featured Tournament</SectionLabel>
        {!loaded || !t ? (
          <div className="mt-8 h-80 rounded-xl bg-[#1a1f2e] animate-pulse" data-testid="featured-skeleton" />
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 relative overflow-hidden rounded-xl border border-white/10"
            data-testid="featured-card"
          >
            {t.banner_url ? (
              <img
                src={bannerSrc(t.banner_url)}
                alt={t.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#1a1f2e] via-[#141824] to-[#2a0d16]" />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/95 via-[#0d0d0d]/75 to-[#0d0d0d]/30" />
            <div className="relative p-7 sm:p-12 max-w-2xl">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusPill status={t.status} />
                {isFree && <FreePill />}
                {t.game_mode && (
                  <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/20 text-[#c9c9c9]">
                    {t.game_mode}
                  </span>
                )}
                {t.map && (
                  <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/20 text-[#c9c9c9]">
                    {t.map}
                  </span>
                )}
              </div>
              <h2 className="mt-6 font-display text-white text-3xl sm:text-5xl leading-tight" data-testid="featured-title">
                {t.title}
              </h2>
              <div className="mt-6 flex flex-wrap items-end gap-x-10 gap-y-4">
                <div>
                  <p className="text-4xl sm:text-5xl font-black italic text-[#e8173a] flex items-center gap-3" data-testid="featured-prize">
                    <Trophy className="w-8 h-8" />
                    ₹{Number(t.prize_pool || 0).toLocaleString('en-IN')}
                  </p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#8a8a8a] font-semibold">Prize Pool</p>
                </div>
                <div>
                  <p className={`text-2xl font-extrabold italic ${isFree ? 'text-[#00c853]' : 'text-white'}`} data-testid="featured-entry">
                    {isFree ? 'FREE' : `₹${Number(t.entry_fee).toLocaleString('en-IN')}`}
                  </p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#8a8a8a] font-semibold">Entry Fee</p>
                </div>
                {t.datetime && (
                  <div>
                    <p className="text-lg font-bold text-white flex items-center gap-2" data-testid="featured-time">
                      <CalendarClock className="w-4 h-4 text-[#e8173a]" />
                      {formatIST(t.datetime)}
                    </p>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#8a8a8a] font-semibold">Starts</p>
                  </div>
                )}
              </div>
              <div className="mt-8 max-w-md">
                <SlotBar joined={t.slots_joined} max={t.max_slots} />
              </div>
              <Link
                to="/tournaments"
                data-testid="featured-view-button"
                className="mt-8 inline-flex items-center gap-2 bg-[#e8173a] hover:bg-[#c0102e] text-white text-sm font-extrabold uppercase tracking-wider px-8 py-4 rounded-lg transition-all duration-300 hover:shadow-[0_15px_40px_-12px_rgba(232,23,58,0.7)] active:scale-95"
              >
                View Tournament
                <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
